import {
	formatMetricKilometres,
	formatMetricKilowattHour,
	formatNumberNoDecimals,
} from '@anwb/poncho/utilities/formatters';
import { createSelector } from 'reselect';
import {
	Color,
	LeaseCarDataPricePoint,
	LeaseCarDataResponseOccasion,
	LeaseCarDataService,
	LeaseCarDurations,
	Option,
	StandardEquipment,
	Store,
	Upgrade,
	VehicleData,
} from 'types';
import {
	areValuesDifferent,
	getCarBodyType,
	getCarFuelType,
	getInteriorFabricFromEquipment,
} from 'utils';
import {
	formatDoorCount,
	formatFuelEfficiency,
	formatFuelEfficiencyEV,
	formatFuelEfficiencyFCEV,
	formatPowerInKilowatt,
} from 'utils/vehicle';

export const leaseCarDataIdSelector = (state: Store): string | undefined => state.leaseCarData?.id;

export const leaseCarDataNameSelector = (state: Store): string =>
	state.leaseCarData?.vehicleData.make && state.leaseCarData?.vehicleData.model
		? `${state.leaseCarData?.vehicleData.make} ${state.leaseCarData?.vehicleData.model}`
		: '-';

export const carTrimLevelSelector = (state: Store): string | undefined =>
	state.leaseCarData?.vehicleData.trimLevel;

export const carBodyAndDoorCountSelector = (state: Store): string => {
	const bodyType = getCarBodyType(state.leaseCarData?.vehicleData.carBodyType);
	const differentDoors = leaseCarHasDifferentDoorsSelector(state);
	const doorCount = formatDoorCount(
		(state.leaseCarData?.vehicleData.doors &&
			parseInt(state.leaseCarData?.vehicleData.doors, 10)) ||
			undefined
	);
	return `${bodyType}${!differentDoors && doorCount ? ` (${doorCount})` : ''}`;
};

export const standardMotorSelector = (state: Store): string => {
	const enginePowerKW: number | undefined =
		parseInt(state.leaseCarData?.vehicleData.enginePowerKW || '', 10) || undefined;
	const enginePowerHP =
		parseInt(state.leaseCarData?.vehicleData.enginePowerHP || '', 10) || undefined;
	return formatPowerInKilowatt(enginePowerKW, {
		horsePower: enginePowerHP,
		fallback: '-',
	}) as string;
};

export const carFuelTypeSelector = (state: Store): string =>
	getCarFuelType(state.leaseCarData?.vehicleData.fuelType);

export const standardEquipmentSelector = (state: Store): StandardEquipment[] | undefined =>
	state.leaseCarData?.vehicleData.standardEquipment;

export const imageUrlSelector = (state: Store): string => state.leaseCarData?.imageUrl?.w380 || '';

export const imageSrcSetSelector = (state: Store): string => {
	const srcSet: string[] = [];
	if (state.leaseCarData?.imageUrl?.w380) {
		srcSet.push(`${state.leaseCarData?.imageUrl.w380} 1x`);
	}
	if (state.leaseCarData?.imageUrl?.w760) {
		srcSet.push(`${state.leaseCarData?.imageUrl.w760} 2x`);
	}
	if (state.leaseCarData?.imageUrl?.w1520) {
		srcSet.push(`${state.leaseCarData?.imageUrl.w1520} 3x`);
		srcSet.push(`${state.leaseCarData?.imageUrl.w1520} 4x`);
	}
	return srcSet.join(', ');
};

/**
 * The base price is the price in the pricePoints with the highest duration and the lowest mileage value
 */
export const basePriceSelector = (state: Store): number =>
	state.leaseCarData?.privateLeaseData.pricePoints.reduce((prev, curr) =>
		prev.pricePerMonth < curr.pricePerMonth ? prev : curr
	).pricePerMonth || 0;

export const leaseCarOptionsSelector = (state: Store): Option[] =>
	state.leaseCarData?.privateLeaseData.options || [];

export const leaseCarServicesSelector = (state: Store): LeaseCarDataService[] =>
	state.leaseCarData?.privateLeaseData.services || [];

export const leaseCarUpgradesSelector = createSelector(
	(state: Store) => state.leaseCarData?.privateLeaseData.upgrades || [],
	(state: Store) => defaultUpgradeSelector(state),
	(upgrades, defaultUpgrade) => [defaultUpgrade, ...upgrades]
);

export const leaseCarInteriorColorSelector = (state: Store): string =>
	state.leaseCarData?.vehicleInteriorColorData?.color || '';

export const leaseCarDefaultInteriorFabric = (state: Store): string => {
	if (state.leaseCarData) {
		return getInteriorFabricFromEquipment(state.leaseCarData?.vehicleData.standardEquipment);
	}
	return '';
};

export const leaseCarHasDifferentTransmissionsSelector = (state: Store): boolean => {
	const upgrades = leaseCarUpgradesSelector(state);
	return areValuesDifferent<Upgrade, VehicleData>(upgrades, 'vehicleData', 'gearType');
};

export const leaseCarHasDifferentDoorsSelector = (state: Store): boolean => {
	const upgrades = leaseCarUpgradesSelector(state);
	return areValuesDifferent<Upgrade, VehicleData>(upgrades, 'vehicleData', 'doors');
};

export const leaseCarHasDifferentInteriorsSelector = (state: Store): boolean => {
	const upgrades = leaseCarUpgradesSelector(state);

	const fabrics = upgrades.map((upgrade) =>
		getInteriorFabricFromEquipment(upgrade.vehicleData.standardEquipment)
	);

	return new Set(fabrics).size > 1;
};

export const defaultUpgradeSelector = createSelector(
	(state: Store) => state.leaseCarData?.vehicleData,
	(state: Store) => state.leaseCarData?.id,
	(vehicleData, id): Upgrade => ({
		altCarType: vehicleData?.type || '',
		description: vehicleData?.type || '',
		foreignOptionId: 0,
		id: parseInt(id || '', 10) || 0,
		linkedVehicles: [],
		monthPrice: 0,
		vehicleData: vehicleData || ({} as VehicleData),
	})
);

export const leaseCarColorsSelector = (store: Store): Color[] =>
	store.leaseCarData?.privateLeaseData.colors || [];

export const leaseCarGroupedPricePointsSelector = (state: Store): LeaseCarDurations | undefined =>
	state.leaseCarData?.privateLeaseData.groupedPricePoints || undefined;

export const currentMileageSelector = (store: Store): string => {
	const currentMileage = (store.leaseCarData as LeaseCarDataResponseOccasion)?.vehicleMetaData
		?.currentMileage;
	return formatNumberNoDecimals(currentMileage, { fallback: '-' }) as string;
};

export const rangeSelector = (store: Store): string => {
	const range = store.leaseCarData?.vehicleData.range;
	return formatMetricKilometres(Number(range), { fallback: '-' }) as string;
};

export const batteryCapacitySelector = (store: Store): string => {
	const batteryCapacity = store.leaseCarData?.vehicleData.batteryCapacity;
	return formatMetricKilowattHour(Number(batteryCapacity), {
		fallback: '-',
	}) as string;
};

export const fuelConsumptionSelector = (store: Store): string => {
	const fuelConsumption = parseFloat(
		store.leaseCarData?.vehicleData.fuelConsumptionCombined || '0'
	);
	const fuelType = store.leaseCarData?.vehicleData.fuelType;
	switch (fuelType) {
		case 'H':
			return formatFuelEfficiencyFCEV(fuelConsumption, {
				fallback: '-',
			}) as string;
		case 'VE':
			return formatFuelEfficiencyEV(fuelConsumption, {
				fallback: '-',
			}) as string;
		default:
			return formatFuelEfficiency(fuelConsumption, { fallback: '-' }) as string;
	}
};

export const leaseCarDataAvailableKilometragesSelector = (state: Store): LeaseCarDataPricePoint[] =>
	state.leaseCarData?.privateLeaseData?.pricePoints || [];
